import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { trackSubscriptionStart } from '../utils/analytics';
import SEO from './SEO';
import ConfirmModal from './ConfirmModal';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './Pricing.css';

const PLAN_LABELS = {
  user: { free: 'Free', standard: 'Standard', premium: 'Premium' },
  affiliate: {
    free: 'Free Creator / Coach',
    standard: 'Standard Creator / Coach',
    pro: 'Pro Creator / Coach'
  },
  business: {
    starter: 'Business Starter',
    professional: 'Business Professional',
    enterprise: 'Business Enterprise'
  }
};

const formatPeriodEnd = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const buildCancelMessage = (flow) => {
  const when = formatPeriodEnd(flow.periodEnd);
  const until = when ? ` until ${when}` : ' until the end of the current billing period';
  if (flow.tierType === 'business') {
    return `You'll keep ${flow.planLabel}${until}. After that, billing stops and the organization returns to Business Starter limits. You can reactivate any time before then.`;
  }
  return `You'll keep ${flow.planLabel}${until}. After that, your account returns to the free plan. You can reactivate any time before then.`;
};

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [cancelFlow, setCancelFlow] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isLoggedIn = !!user;
  const isAffiliate = user?.isAffiliate;

  useEffect(() => {
    fetchConfig();
    if (isLoggedIn) {
      fetchSubscriptionStatus();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, '');
    if (!raw) return;
    const t = setTimeout(() => {
      const el = document.getElementById(raw);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [location.hash, location.pathname]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // First sync with Stripe to ensure we have the latest status
      // This handles cases where webhook events were missed
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/subscription/sync`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (syncError) {
        console.log('Sync skipped:', syncError.message);
      }
      
      // Then fetch the status
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // After login, check if there's a pending subscription to process
  useEffect(() => {
    if (isLoggedIn) {
      const pending = localStorage.getItem('pendingSubscription');
      if (pending) {
        try {
          const { tierType, tier } = JSON.parse(pending);
          localStorage.removeItem('pendingSubscription');
          // Small delay to let the component fully mount and config load
          const timer = setTimeout(() => {
            handleSubscribe(tierType, tier);
          }, 500);
          return () => clearTimeout(timer);
        } catch (e) {
          localStorage.removeItem('pendingSubscription');
        }
      }
    }
  }, [isLoggedIn]);

  const handleSubscribe = async (tierType, tier) => {
    if (!isLoggedIn) {
      // Store subscription intent and redirect to sign in
      localStorage.setItem('pendingSubscription', JSON.stringify({ tierType, tier }));
      toast('Please sign in to subscribe. You\'ll be redirected to complete your subscription.', { icon: 'ℹ️' });
      navigate('/');
      return;
    }

    // Check if user is already on this plan
    if (tierType === 'user' && subscriptionStatus?.user?.tier === tier) {
      toast.info('You are already on this plan');
      return;
    }
    if (tierType === 'affiliate' && subscriptionStatus?.affiliate?.tier === tier) {
      toast.info('You are already on this plan');
      return;
    }

    // Check if user needs to be an affiliate first
    if (tierType === 'affiliate' && !isAffiliate) {
      toast.error('Please register as an affiliate first');
      navigate('/affiliate/register');
      return;
    }

    setLoading(true);
    setSelectedPlan(`${tierType}_${tier}`);
    trackSubscriptionStart(`${tierType}_${tier}`);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tierType, tier })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error(error.message || 'Failed to start checkout');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleManageSubscription = async (tierType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tierType })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Check if this is a stale Stripe account issue
        if (data.stripeAccountChanged) {
          toast.error('Your subscription data has been reset. Please subscribe again.');
          // Refresh the page to show updated state
          fetchSubscriptionStatus();
          return;
        }
        throw new Error(data.error || 'Failed to open billing portal');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error(error.message || 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  const openCancelFlow = (tierType) => {
    const subscription = subscriptionStatus?.[tierType];
    const tier = subscription?.tier;
    setCancelFlow({
      tierType,
      planLabel: PLAN_LABELS[tierType]?.[tier] || 'your plan',
      periodEnd: subscription?.currentPeriodEnd || null
    });
  };

  const handleConfirmCancel = async () => {
    if (!cancelFlow) return;

    setCancelLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tierType: cancelFlow.tierType })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      const when = formatPeriodEnd(data.cancelAt);
      toast.success(
        when
          ? `Subscription will cancel on ${when}. You'll keep access until then.`
          : 'Subscription will cancel at the end of the billing period.'
      );
      setCancelFlow(null);
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async (tierType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tierType })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reactivate subscription');
      }

      toast.success('Subscription reactivated successfully!');
      // Refresh status
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.message || 'Failed to reactivate subscription');
    } finally {
      setLoading(false);
    }
  };

  const renderActivePlanActions = (tierType, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return (
        <button
          className="reactivate-btn"
          onClick={() => handleReactivate(tierType)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Reactivate Subscription'}
        </button>
      );
    }

    return (
      <div className="plan-action-stack">
        <button
          className="manage-btn"
          onClick={() => handleManageSubscription(tierType)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Manage Subscription'}
        </button>
        <button
          type="button"
          className="cancel-subscription-btn"
          onClick={() => openCancelFlow(tierType)}
          disabled={loading}
        >
          Cancel Subscription
        </button>
      </div>
    );
  };

  const currentUserTier = subscriptionStatus?.user?.tier || 'free';
  const currentAffiliateTier = subscriptionStatus?.affiliate?.tier || 'free';
  const userCancelAtPeriodEnd = subscriptionStatus?.user?.cancelAtPeriodEnd;
  const affiliateCancelAtPeriodEnd = subscriptionStatus?.affiliate?.cancelAtPeriodEnd;
  const userPeriodEnd = subscriptionStatus?.user?.currentPeriodEnd;

  const userPlans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      interval: 'forever',
      description: 'Get started with meal planning & fitness',
      features: [
        '3 Personal Menus',
        '3 Saved Workouts',
        '1 Custom Program',
        '1 Nutrition plan',
        '3 Shopping lists',
        'Browse all public food & fitness content',
        '10 AI Coach messages/month'
      ],
      cta: currentUserTier === 'free' ? 'Current Plan' : 'Downgrade',
      disabled: currentUserTier === 'free',
      tierType: 'user',
      tier: 'free'
    },
    {
      id: 'standard',
      name: 'Standard',
      price: '$5',
      interval: '/month',
      description: 'Meal planning + fitness tracking',
      trialDays: 7,
      features: [
        '7-day free trial',
        '25 Personal Menus',
        '15 Saved Workouts',
        '5 Custom Programs',
        '5 Nutrition plans',
        '25 Saved shopping lists',
        '100 AI Coach messages/month',
        'More room to build and organize',
        'Support portal & ticketing'
      ],
      popular: true,
      cta: currentUserTier === 'standard' ? 'Current Plan' : 'Start Free Trial',
      disabled: currentUserTier === 'standard',
      tierType: 'user',
      tier: 'standard'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$15',
      interval: '/month',
      description: 'Full nutrition + fitness access',
      features: [
        '500 Personal Menus',
        '999 Saved Workouts',
        '100 Custom Programs',
        '50 Nutrition plans',
        '100 Saved shopping lists',
        '500 AI Coach messages/month',
        'Highest personal storage limits',
        'Best fit for power users',
        'Priority support (high & urgent)'
      ],
      cta: currentUserTier === 'premium' ? 'Current Plan' : 'Subscribe',
      disabled: currentUserTier === 'premium',
      tierType: 'user',
      tier: 'premium'
    }
  ];

  const affiliatePlans = [
    {
      id: 'affiliate-free',
      name: 'Free Creator / Coach',
      price: '$0',
      interval: 'forever',
      description: 'Start your creator or coaching journey',
      features: [
        '5 Public Menus',
        '2% Instacart commission on shared recipes',
        '1 Client slot',
        '1 Client nutrition plan',
        'Publish workouts & programs',
        '10 AI Coach messages/month',
        'Basic analytics'
      ],
      cta: !isAffiliate ? 'Become a Creator / Coach' : (currentAffiliateTier === 'free' ? 'Current Plan' : 'Downgrade'),
      disabled: isAffiliate && currentAffiliateTier === 'free',
      tierType: 'affiliate',
      tier: 'free',
      requiresAffiliate: true
    },
    {
      id: 'affiliate-standard',
      name: 'Standard Creator / Coach',
      price: '$5',
      interval: '/month',
      description: 'For growing coaches and creators',
      features: [
        '50 Public Menus',
        '3% Instacart commission on shared recipes',
        '10 Client slots',
        '10 Client nutrition plans',
        'Client payment collection via Stripe',
        '200 AI Coach messages/month',
        'Basic analytics',
        'Support portal & ticketing'
      ],
      popular: true,
      cta: !isAffiliate ? 'Become a Creator First' : (currentAffiliateTier === 'standard' ? 'Current Plan' : 'Subscribe'),
      disabled: !isAffiliate || currentAffiliateTier === 'standard',
      tierType: 'affiliate',
      tier: 'standard',
      requiresAffiliate: true
    },
    {
      id: 'affiliate-pro',
      name: 'Pro Creator / Coach',
      price: '$15',
      interval: '/month',
      description: 'For professional coaches and content creators',
      features: [
        '500 Public Menus',
        '4% Instacart commission on shared recipes',
        '100 Personal Menus (bonus!)',
        '10+ Client slots',
        '10+ Client nutrition plans',
        'Client payment collection via Stripe',
        '1,000 AI Coach messages/month',
        'Advanced analytics',
        'Priority support (high & urgent)'
      ],
      cta: !isAffiliate ? 'Become a Creator First' : (currentAffiliateTier === 'pro' ? 'Current Plan' : 'Subscribe'),
      disabled: !isAffiliate || currentAffiliateTier === 'pro',
      tierType: 'affiliate',
      tier: 'pro',
      requiresAffiliate: true
    }
  ];

  const seo = PAGE_SEO['/pricing'];
  const pricingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'FreshFitLab Subscriptions',
    description:
      'FreshFitLab plans for individuals, creators/coaches, and businesses. Transparent monthly pricing.',
    brand: { '@type': 'Brand', name: 'FreshFitLab' },
    offers: [
      { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Standard', price: '5', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Premium', price: '15', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Business Starter', price: '29', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Business Professional', price: '79', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Business Enterprise', price: '199', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
    ],
  };

  return (
    <div className="pricing-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/pricing"
        jsonLd={[
          pricingJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Pricing', url: '/pricing' },
          ]),
        ]}
      />
      <div className="pricing-header">
        <h1>Simple, Transparent Pricing</h1>
        <p>Choose the plan that fits your needs. Upgrade or downgrade anytime.</p>
      </div>

      {/* User Subscription Section */}
      <section id="pricing-personal" className="pricing-section">
        <h2>Personal Plans</h2>
        <p className="section-subtitle">For individuals who want to organize their meals, recipes, and fitness goals</p>
        
        <div className="pricing-grid">
          {userPlans.map((plan) => {
            // Only show "Your Plan" badge for personal plans if user is NOT an affiliate
            // Affiliates see their plan badge only in the Creator Plans section
            const showYourPlanBadge = !isAffiliate && currentUserTier === plan.tier;
            const isCurrentPlan = currentUserTier === plan.tier;
            
            return (
            <div 
              key={plan.id} 
              className={`pricing-card ${plan.popular ? 'popular' : ''} ${showYourPlanBadge ? 'current' : ''}`}
            >
              {plan.trialDays && !showYourPlanBadge && currentUserTier === 'free' && <div className="popular-badge trial-badge">{plan.trialDays}-Day Free Trial</div>}
              {plan.popular && !(plan.trialDays && !showYourPlanBadge && currentUserTier === 'free') && <div className="popular-badge">Most Popular</div>}
              {showYourPlanBadge && <div className="current-badge">Your Plan</div>}
              
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="interval">{plan.interval}</span>
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <i className="fas fa-check"></i>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Show cancellation notice */}
              {isCurrentPlan && userCancelAtPeriodEnd && plan.tier !== 'free' && (
                <div className="cancellation-notice">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Cancels on {new Date(userPeriodEnd).toLocaleDateString()}</span>
                </div>
              )}

              <div className="plan-cta">
                {isCurrentPlan && plan.tier !== 'free' ? (
                  renderActivePlanActions('user', userCancelAtPeriodEnd)
                ) : plan.tier === 'free' && userCancelAtPeriodEnd ? (
                  /* During cancellation period, show info instead of disabled downgrade button */
                  <div className="downgrade-info">
                    <p>You'll be downgraded automatically on {new Date(userPeriodEnd).toLocaleDateString()}</p>
                  </div>
                ) : plan.tier === 'free' && currentUserTier !== 'free' ? (
                  <button
                    type="button"
                    className="subscribe-btn"
                    onClick={() => openCancelFlow('user')}
                    disabled={loading}
                  >
                    Downgrade
                  </button>
                ) : (
                  <button
                    className={`subscribe-btn ${plan.disabled ? 'disabled' : ''}`}
                    onClick={() => plan.tier !== 'free' && handleSubscribe(plan.tierType, plan.tier)}
                    disabled={plan.disabled || loading || plan.tier === 'free'}
                  >
                    {loading && selectedPlan === `${plan.tierType}_${plan.tier}` ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      plan.cta
                    )}
                  </button>
                )}
              </div>
            </div>
          )})}
        </div>
      </section>

      {/* Affiliate Subscription Section */}
      <section id="pricing-creator" className="pricing-section affiliate-section">
        <h2>Creator / Coach Plans</h2>
        <p className="section-subtitle">For menu creators, fitness coaches, and content creators who want to publish recipes, train clients, and earn commissions</p>
        
        <div className="pricing-grid three-col">
          {affiliatePlans.map((plan) => {
            const isCurrentAffiliatePlan = isAffiliate && currentAffiliateTier === plan.tier;
            
            return (
            <div 
              key={plan.id} 
              className={`pricing-card ${plan.popular ? 'popular' : ''} ${isCurrentAffiliatePlan ? 'current' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Best Value</div>}
              {isCurrentAffiliatePlan && <div className="current-badge">Your Plan</div>}
              
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="interval">{plan.interval}</span>
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <i className="fas fa-check"></i>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Show cancellation notice for affiliate */}
              {isCurrentAffiliatePlan && affiliateCancelAtPeriodEnd && plan.tier !== 'free' && (
                <div className="cancellation-notice">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Cancels on {new Date(subscriptionStatus?.affiliate?.currentPeriodEnd).toLocaleDateString()}</span>
                </div>
              )}

              <div className="plan-cta">
                {!isAffiliate && plan.tier === 'free' ? (
                  <button 
                    className="subscribe-btn"
                    onClick={() => navigate('/affiliate/register')}
                  >
                    Become a Creator / Coach
                  </button>
                ) : isCurrentAffiliatePlan && plan.tier !== 'free' ? (
                  renderActivePlanActions('affiliate', affiliateCancelAtPeriodEnd)
                ) : plan.tier === 'free' && isAffiliate && affiliateCancelAtPeriodEnd ? (
                  /* During cancellation period, show info instead of disabled downgrade button */
                  <div className="downgrade-info">
                    <p>You'll be downgraded automatically on {new Date(subscriptionStatus?.affiliate?.currentPeriodEnd).toLocaleDateString()}</p>
                  </div>
                ) : plan.tier === 'free' && isAffiliate && currentAffiliateTier !== 'free' ? (
                  <button
                    type="button"
                    className="subscribe-btn"
                    onClick={() => openCancelFlow('affiliate')}
                    disabled={loading}
                  >
                    Downgrade
                  </button>
                ) : (
                  <button
                    className={`subscribe-btn ${plan.disabled ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!isAffiliate) {
                        navigate('/affiliate/register');
                      } else if (plan.tier !== 'free') {
                        handleSubscribe(plan.tierType, plan.tier);
                      }
                    }}
                    disabled={(isAffiliate && plan.disabled) || loading || (isAffiliate && plan.tier === 'free')}
                  >
                    {loading && selectedPlan === `${plan.tierType}_${plan.tier}` ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      plan.cta
                    )}
                  </button>
                )}
              </div>
            </div>
          )})}
        </div>
      </section>

      {/* Business Subscription Section */}
      <section id="pricing-business" className="pricing-section business-section">
        <h2>Business Plans</h2>
        <p className="section-subtitle">For physical therapy clinics, nutrition practices, wellness centers, and other businesses that manage clients</p>
        
        <div className="pricing-grid three-col">
          {[
            {
              id: 'business-starter',
              name: 'Business Starter',
              price: '$29',
              interval: '/month',
              description: 'For small practices getting started',
              trialDays: 14,
              features: [
                '14-day free trial',
                'Up to 5 employees',
                'Up to 50 clients',
                '10 clients per employee',
                'Client exercise & nutrition assignment',
                'QR code & invite link onboarding',
                'Business admin dashboard',
                'Employee client dashboards',
                'Clients get Standard-tier limits'
              ],
              tierType: 'business',
              tier: 'starter'
            },
            {
              id: 'business-professional',
              name: 'Business Professional',
              price: '$79',
              interval: '/month',
              description: 'For growing practices and clinics',
              features: [
                'Up to 25 employees',
                'Up to 250 clients',
                '25 clients per employee',
                'Client exercise & nutrition assignment',
                'QR code & invite link onboarding',
                'Business admin dashboard',
                'Employee client dashboards',
                'Clients get Premium-tier limits',
                'Priority support'
              ],
              popular: true,
              tierType: 'business',
              tier: 'professional'
            },
            {
              id: 'business-enterprise',
              name: 'Business Enterprise',
              price: '$199',
              interval: '/month',
              description: 'For large organizations',
              features: [
                'Unlimited employees',
                'Unlimited clients',
                'Client exercise & nutrition assignment',
                'QR code & invite link onboarding',
                'Business admin dashboard',
                'Employee client dashboards',
                'Clients get Premium-tier limits',
                'SSO / SAML integration',
                'Dedicated support'
              ],
              tierType: 'business',
              tier: 'enterprise'
            }
          ].map((plan) => {
            const currentBusinessTier = subscriptionStatus?.business?.tier;
            const businessCancelAtPeriodEnd = subscriptionStatus?.business?.cancelAtPeriodEnd;
            const businessPeriodEnd = subscriptionStatus?.business?.currentPeriodEnd;
            const isCurrentPlan = user?.isBusinessOwner && currentBusinessTier === plan.tier;
            
            return (
              <div 
                key={plan.id} 
                className={`pricing-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current' : ''}`}
              >
                {plan.trialDays && !isCurrentPlan && <div className="popular-badge trial-badge">{plan.trialDays}-Day Free Trial</div>}
                {plan.popular && !plan.trialDays && <div className="popular-badge">Best Value</div>}
                {isCurrentPlan && <div className="current-badge">Your Plan</div>}
                
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="plan-price">
                    <span className="price">{plan.price}</span>
                    <span className="interval">{plan.interval}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <i className="fas fa-check"></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Show cancellation notice for business */}
                {isCurrentPlan && businessCancelAtPeriodEnd && (
                  <div className="cancellation-notice">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Cancels on {new Date(businessPeriodEnd).toLocaleDateString()}</span>
                  </div>
                )}

                <div className="plan-cta">
                  {isCurrentPlan ? (
                    renderActivePlanActions('business', businessCancelAtPeriodEnd)
                  ) : user?.isBusinessOwner && plan.tier === 'starter' && businessCancelAtPeriodEnd ? (
                    <div className="downgrade-info">
                      <p>You'll be downgraded automatically on {new Date(businessPeriodEnd).toLocaleDateString()}</p>
                    </div>
                  ) : user?.isBusinessOwner ? (
                    <button
                      className="subscribe-btn"
                      onClick={() => handleSubscribe('business', plan.tier)}
                      disabled={loading}
                    >
                      {loading && selectedPlan === `business_${plan.tier}` ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        currentBusinessTier ? 'Change Plan' : (plan.trialDays ? 'Start Free Trial' : 'Subscribe')
                      )}
                    </button>
                  ) : (
                    <button
                      className="subscribe-btn"
                      onClick={() => navigate('/business/register')}
                    >
                      {plan.trialDays ? 'Start Free Trial' : 'Register Your Business'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I cancel anytime?</h4>
            <p>Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
          </div>
          <div className="faq-item">
            <h4>What payment methods do you accept?</h4>
            <p>We accept all major credit cards through our secure payment processor, Stripe.</p>
          </div>
          <div className="faq-item">
            <h4>What happens to my menus if I downgrade?</h4>
            <p>Your existing menus remain accessible. You just won't be able to create new ones beyond your plan's limit.</p>
          </div>
          <div className="faq-item">
            <h4>How does affiliate commission work?</h4>
            <p>As an affiliate, you earn commission on Instacart purchases made through your shared recipes. Free Creator / Coach earns 2%, Standard earns 3%, and Pro earns 4%.</p>
          </div>
        </div>
        <div className="pricing-faq-link-row">
          <p>Need the full breakdown of plans, creator tools, and billing details?</p>
          <button type="button" className="pricing-faq-link" onClick={() => navigate('/faq')}>
            View Full FAQ
          </button>
        </div>
      </section>

      {/* Usage Stats (if logged in) */}
      {isLoggedIn && subscriptionStatus && (
        <section className="usage-section">
          <h2>Your Current Usage</h2>
          
          {/* Personal Plan Usage */}
          <h3 className="usage-section-title">Personal Plan</h3>
          <div className="usage-grid">
            <div className="usage-card">
              <h4>Personal Menus</h4>
              <div className="usage-bar">
                <div 
                  className="usage-fill" 
                  style={{ 
                    width: `${Math.min(100, (subscriptionStatus.user?.usage?.menuCount || 0) / (subscriptionStatus.user?.limits?.maxMenus || 1) * 100)}%` 
                  }}
                />
              </div>
              <span className="usage-text">
                {subscriptionStatus.user?.usage?.menuCount || 0} / {subscriptionStatus.user?.limits?.maxMenus || 3}
              </span>
            </div>
          </div>
          
          {/* Affiliate/Creator Plan Usage - only show if user is an affiliate */}
          {subscriptionStatus.isAffiliate && subscriptionStatus.affiliate && (
            <>
              <h3 className="usage-section-title">Creator Plan</h3>
              <div className="usage-grid">
                <div className="usage-card">
                  <h4>Public Menus</h4>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill" 
                      style={{ 
                        width: `${Math.min(100, (subscriptionStatus.affiliate?.usage?.menuCount || 0) / (subscriptionStatus.affiliate?.limits?.maxMenus || 1) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="usage-text">
                    {subscriptionStatus.affiliate?.usage?.menuCount || 0} / {subscriptionStatus.affiliate?.limits?.maxMenus || 10}
                  </span>
                </div>
                <div className="usage-card">
                  <h4>AI Generations (today)</h4>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill" 
                      style={{ 
                        width: `${Math.min(100, (subscriptionStatus.affiliate?.usage?.aiGenerationsToday || 0) / Math.max(1, subscriptionStatus.affiliate?.limits?.maxAIGenerationsPerDay || 1) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="usage-text">
                    {subscriptionStatus.affiliate?.usage?.aiGenerationsToday || 0} / {subscriptionStatus.affiliate?.limits?.maxAIGenerationsPerDay || 5}
                  </span>
                </div>
                <div className="usage-card">
                  <h4>Image Uploads (today)</h4>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill" 
                      style={{ 
                        width: `${Math.min(100, (subscriptionStatus.affiliate?.usage?.imageUploadsToday || 0) / Math.max(1, subscriptionStatus.affiliate?.limits?.maxImageUploadsPerDay || 1) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="usage-text">
                    {subscriptionStatus.affiliate?.usage?.imageUploadsToday || 0} / {subscriptionStatus.affiliate?.limits?.maxImageUploadsPerDay || 10}
                  </span>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      <ConfirmModal
        isOpen={!!cancelFlow}
        title="Cancel subscription?"
        message={cancelFlow ? buildCancelMessage(cancelFlow) : ''}
        confirmLabel="Cancel subscription"
        cancelLabel="Keep subscription"
        danger
        loading={cancelLoading}
        loadingLabel="Canceling…"
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          if (!cancelLoading) setCancelFlow(null);
        }}
      />
    </div>
  );
};

export default Pricing;
